import { createRootRouteWithContext, createRoute, redirect, Outlet } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@eon/api-client';
import { ORG_OPERATOR_ROLES, SYSTEM_ADMIN_ROLES } from '@eon/types';
import { ensureRole } from './auth/guards';
import { AppLayout } from './layouts/AppLayout';
import {
  DashboardRoute,
  DevicesRoute,
  PortalStationDetailRoute,
  PortalStationEditRoute,
  PortalStationNewRoute,
  SessionsRoute,
  StationsRoute,
  UsersRoute,
} from './routing/sharedRoutes';
import { LoginPage } from './pages/LoginPage';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { DeviceNewPage } from './pages/admin/DeviceNewPage';
import { DeviceDetailPage } from './pages/admin/DeviceDetailPage';
import { DeviceControlPage } from './pages/admin/DeviceControlPage';
import { DeviceConsolePage } from './pages/admin/DeviceConsolePage';
import { UserDetailPage } from './pages/admin/UserDetailPage';
import { UserEditPage, UserNewPage } from './pages/admin/UserEditPage';
import { UserInvitePage } from './pages/admin/UserInvitePage';
import { GroupsPage } from './pages/admin/GroupsPage';
import { GroupDetailPage } from './pages/admin/GroupDetailPage';
import { IdTagsPage } from './pages/admin/IdTagsPage';
import { MapPage } from './pages/admin/MapPage';
import { LogsPage } from './pages/admin/LogsPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { ProfilePage } from './pages/portal/ProfilePage';
import { NotificationsPage } from './pages/portal/NotificationsPage';

interface RouterCtx {
  queryClient: QueryClient;
}

function adminGuard({ context }: { context: RouterCtx }) {
  return ensureRole(context.queryClient, SYSTEM_ADMIN_ROLES);
}

function portalGuard({ context }: { context: RouterCtx }) {
  return ensureRole(context.queryClient, ORG_OPERATOR_ROLES);
}

const rootRoute = createRootRouteWithContext<RouterCtx>()({
  component: Outlet,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: AppLayout,
  beforeLoad: () => {
    if (!getAccessToken()) {
      throw redirect({ to: '/login' });
    }
  },
});

const forbiddenRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/forbidden',
  component: ForbiddenPage,
});

const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: DashboardRoute,
});

const devicesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/devices',
  component: DevicesRoute,
});

const deviceNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/devices/new',
  beforeLoad: adminGuard,
  component: DeviceNewPage,
});

const deviceDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/devices/$deviceId',
  beforeLoad: adminGuard,
  component: DeviceDetailPage,
});

const deviceControlRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/devices/$deviceId/control',
  beforeLoad: adminGuard,
  component: DeviceControlPage,
});

const deviceConsoleRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/devices/$deviceId/console',
  beforeLoad: adminGuard,
  component: DeviceConsolePage,
});

const stationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/stations',
  component: StationsRoute,
});

const stationNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/stations/new',
  component: PortalStationNewRoute,
});

const stationDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/stations/$stationId',
  component: PortalStationDetailRoute,
});

const stationEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/stations/$stationId/edit',
  component: PortalStationEditRoute,
});

const sessionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sessions',
  component: SessionsRoute,
});

const usersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/users',
  component: UsersRoute,
});

const userNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/users/new',
  beforeLoad: adminGuard,
  component: UserNewPage,
});

const userInviteRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/users/invite',
  beforeLoad: adminGuard,
  component: UserInvitePage,
});

const userDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/users/$userId',
  beforeLoad: adminGuard,
  component: UserDetailPage,
});

const userEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/users/$userId/edit',
  beforeLoad: adminGuard,
  component: UserEditPage,
});

const groupsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/groups',
  beforeLoad: adminGuard,
  component: GroupsPage,
});

const groupDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/groups/$groupId',
  beforeLoad: adminGuard,
  component: GroupDetailPage,
});

const idTagsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/id-tags',
  beforeLoad: adminGuard,
  component: IdTagsPage,
});

const mapRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/map',
  beforeLoad: adminGuard,
  component: MapPage,
});

const logsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/logs',
  beforeLoad: adminGuard,
  component: LogsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings',
  beforeLoad: adminGuard,
  component: SettingsPage,
});

const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/profile',
  beforeLoad: portalGuard,
  component: ProfilePage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/notifications',
  beforeLoad: portalGuard,
  component: NotificationsPage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    forbiddenRoute,
    indexRoute,
    devicesRoute,
    deviceNewRoute,
    deviceDetailRoute,
    deviceControlRoute,
    deviceConsoleRoute,
    stationsRoute,
    stationNewRoute,
    stationDetailRoute,
    stationEditRoute,
    sessionsRoute,
    usersRoute,
    userNewRoute,
    userInviteRoute,
    userDetailRoute,
    userEditRoute,
    groupsRoute,
    groupDetailRoute,
    idTagsRoute,
    mapRoute,
    logsRoute,
    settingsRoute,
    profileRoute,
    notificationsRoute,
  ]),
]);
